// Single source of truth.
//
// Verification policy (re-asserted 2026-05-05):
//   "verified-original"  → published on ocoeeriverexperience.com or in the
//                          business's own public listings.
//   "public-fact"        → independently verifiable (e.g. 1996 Olympic venue,
//                          Cherokee National Forest geography, TVA release
//                          schedule, US Hwy 64 coordinates).
//   "NEEDS-VERIFY"       → inferred or assumed during development. UI must
//                          either hide these or render a "call to confirm"
//                          fallback. Do not show specific numbers as fact.
//
// If you can't tag a value with one of the first two, it is "NEEDS-VERIFY"
// by default and any specific copy must be softened to "call us".

// Master switch for content that is illustrative only — fictional reviews,
// guide bios, "recent bookings" social proof, dynamic price ticker, etc.
// MUST stay false until the business provides verified data and signs off.
export const DEMO_CONTENT = false;

export const business = {
  name: "Ocoee River Experience",
  tagline: "Whitewater rafting, on-site lodging, and dining near the Ocoee River.",
  // Founding year intentionally omitted — original site does not state it.
  // foundedYear intentionally left out until confirmed.
  phone: "(423) 769-1036",
  phoneRaw: "+14237691036",
  // Normalized to lowercase (RFC 5321 — local part may be case-sensitive but
  // virtually no providers enforce it; lowercase reads cleaner everywhere).
  email: "info@ocoeeriverexperience.com",
  address: {
    street: "414 Parksville Road",
    streetShort: "414 Parksville Road",
    city: "Benton",
    state: "TN",
    zip: "37307",
    full: "414 Parksville Road, Benton, TN 37307",
    locationNote: "Located just before Parksville Lake",
  },
  geo: {
    // Verified from original site
    lat: 35.112252,
    lng: -84.65531,
  },
  social: {
    instagram: "https://instagram.com/ocoeeriverexperience",
    facebook:  "https://facebook.com/OcoeeRiverExperience",
    twitter:   "https://x.com/ocoeeriverTN",
    tiktok:    "https://tiktok.com/@ocoeeriver",
    // YouTube/LinkedIn omitted — original links were broken Wix placeholders.
  },
  bookingProvider: "TheFlyBook",
  // Russ's feedback (2026-05-23): the catalog landing
  // (https://go.theflybook.com/book/1452 — without an EntityConfig)
  // renders as a BLANK page in production — Flybook's hash-routed SPA
  // doesn't have a published "list all products" view at the base
  // route. The site's primary CTAs (Hero Book Now, MobileBottomBar
  // Book, header pill) all funneled visitors there and stranded them.
  // Now `bookingBase` lands directly on Classic Middle Ocoee — the
  // primary product. Per-route pages still override with their own
  // entity (lodge 33052, bunkhouse 33053, camping sub-products, etc.)
  // via pages[slug].bookingUrl, so this only affects the generic CTAs.
  bookingBase: "https://go.theflybook.com/book/1452/EntityConfig/32930",
  // Legacy Wix-hosted merch store. Still uses the default Wix demo catalogue
  // (placeholder products like "i-m-a-product-1") at audit time — we link
  // out to it but do not mirror its data. Replace with the real shop URL or
  // remove once merch ships from this site.
  // Apex domain is canonical post-migration (2026-05-23). www.* now
  // 308-redirects to apex at the Vercel edge.
  shopUrl: "https://ocoeeriverexperience.com/shop",
  // NEEDS-VERIFY: confirm the operator actually answers WhatsApp at the same
  // line as the office phone before flipping this true. Until then the FAB
  // stays hidden so we don't promise a channel that may not be staffed.
  whatsappEnabled: false,
  amenities: [
    "Free on-site parking",
    "Conveniently located near the Ocoee River",
    "Short distance to Parksville Lake and the Ocoee River",
  ],
  // Public-knowledge rapids on the Middle Ocoee, in standard run order.
  middleOcoeeRapids: [
    "Grumpy's",
    "Broken Nose",
    "Slice & Dice",
    "Double Suck",
    "Tablesaw",
    "Diamond Splitter",
    "Hell Hole",
    "Powerhouse",
  ],
  // Storefront / front desk hours (per the original homepage).
  // Rafting trip hours run on the TVA release schedule and may differ.
  hours: [
    { day: "Saturday",  short: "Sat", open: "9:00 AM", close: "7:00 PM" },
    { day: "Sunday",    short: "Sun", open: "9:00 AM", close: "7:00 PM" },
    { day: "Monday",    short: "Mon", open: "9:00 AM", close: "5:00 PM" },
    { day: "Tuesday",   short: "Tue", closed: true },
    { day: "Wednesday", short: "Wed", closed: true },
    { day: "Thursday",  short: "Thu", open: "9:00 AM", close: "5:00 PM" },
    { day: "Friday",    short: "Fri", open: "9:00 AM", close: "5:00 PM" },
  ] as Array<{ day: string; short: string; open?: string; close?: string; closed?: boolean }>,
  seasonNote:
    "Operating season follows the Ocoee River's dam-release calendar. Confirm release dates before booking.",
} as const;

// Rates and inventory below were sourced from the original site's sub-pages
// during early scraping. Re-verify with the operator before launch — pricing
// and room composition are operationally sensitive and easy to drift.
//   NEEDS-VERIFY: lodge.baseRate, lodge.extraGuestRate, lodge.minNights
//   NEEDS-VERIFY: lodge.rooms[*] (room names, bed configs, occupancy)
//   NEEDS-VERIFY: lodge.amenities (which appliances each room actually has)
//   NEEDS-VERIFY: lodge.bookingUrl (entity-specific URL)
export const lodge = {
  baseRate: 75,
  extraGuestRate: 10,
  minNights: 2,
  rooms: [
    { name: "Single Room",  beds: "1 Queen",                 occupancy: "1 (max 2)",  notes: "Rooms 1 & 4" },
    { name: "Double Room",  beds: "2 beds",                  occupancy: "2 (max 4)",  notes: "Standard double" },
    { name: "Family Room",  beds: "1 Queen + 1 Bunk",        occupancy: "1 (max 4)",  notes: "Rooms 11 & 12" },
  ],
  amenities: [
    "Climate controlled (heat & A/C)",
    "Mini fridge",
    "Microwave",
    "Coffee maker",
    "TV",
    "Free Wi-Fi",
    "Private bathroom",
  ],
  // Lodging entity is 33052. We previously linked 33050 by mistake; that ID
  // is actually "RV Sites Power & Water" in the camping catalog. Confirmed
  // by inspecting the live ocoeeriverexperience.com /general-1 page (the
  // "Click Here to Reserve" button on Lodge points to EntityConfig/33052).
  bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33052",
};

// NEEDS-VERIFY: bunkhouse capacity, perks, ladies-only room number, and
// the entity-specific booking URL. Re-confirm with the operator.
export const bunkhouse = {
  rooms: 11,
  bedsPerRoom: 4,
  totalBunks: 44,
  perks: [
    "Air conditioning & heat",
    "Bring your own bedding",
    "Book a single bunk (communal) or all 4 for a private room",
  ],
  ladiesOnlyRoom: 26,
  bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33053",
};

// Camping prices are NEEDS-VERIFY upstream (the live ocoeeriverexperience.com
// site shows them inside the Flybook widget; we don't re-state them here).
// Entity IDs were extracted from the "Click Here to Reserve" hrefs on the
// /camping-rv page of the production site. Pavillions kept with the live
// site's spelling ("Pavillions", not "Pavilions") to match what shows up in
// the Flybook catalog.
export const camping = {
  options: [
    {
      name: "RV · 50A with Water & Sewer",
      slug: "rv-50amp",
      unit: "night",
      entityId: "32944",
      bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/32944",
    },
    {
      name: "RV · Power & Water",
      slug: "rv-power-water",
      unit: "night",
      entityId: "33050",
      bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33050",
    },
    {
      name: "Tent site",
      slug: "tent",
      unit: "person · night",
      entityId: "33051",
      bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33051",
    },
    {
      name: "Pavillions",
      slug: "pavillions",
      unit: "night",
      entityId: "33054",
      bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33054",
    },
  ],
};

// NEEDS-VERIFY: the menu items and the operating hours for Sugarloaf.
// The original homepage only names the venue; this data was inferred during
// development. Confirm with the kitchen before launch.
export const sugarloaf = {
  name: "Sugarloaf Bar and Grill",
  location: "On-site at 414 Parksville Road",
  serves: ["Hearty breakfast", "To-go lunch", "Burgers", "BBQ", "Chicken", "Soft drinks", "Beer"],
  hours: [
    { day: "Friday",   open: "12:00 PM", close: "10:00 PM" },
    { day: "Saturday", open: "8:00 AM",  close: "10:00 PM" },
    { day: "Sunday",   open: "8:00 AM",  close: "6:00 PM" },
  ],
};

// NEEDS-VERIFY: address, hours, menu, and perks. The original site only
// names the restaurant. The "Tennessee-style smoked BBQ" descriptor used
// elsewhere should be replaced with the venue's own self-description once
// known. The "next to McDonald's" landmark was removed per the copy audit —
// it read as confusing ("Blue Hole BBQ at McDonald's") and added no value.
export const bbq = {
  name: "Blue Hole BBQ",
  // This is a separate location, NOT at the lodge campus.
  address: "130 US Highway 64, Ocoee, TN 37361",
  hours: "Tuesday – Sunday · 11:00 AM – 8:00 PM",
  menu: [
    "Smoked brisket",
    "St. Louis-style ribs",
    "Pulled pork",
    "Smoked chicken",
    "Smoked wings",
    "House BBQ sauces",
    "Banana pudding",
    "Peanut butter pie",
  ],
  perks: [
    "Pet-friendly outdoor patio",
    "Order online for pickup",
  ],
};

// ----- Trips -----
export const trips = [
  {
    slug: "middle-ocoee",
    name: "Middle Ocoee",
    description:
      "The classic 5-mile Class III–IV run — guided, gear included, group discounts at checkout.",
    bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/32930",
  },
  {
    slug: "olympic-and-middle",
    name: "Olympic + Middle Ocoee · Full River",
    description:
      "Combined run: the 1996 Olympic course (Upper) plus the Middle Ocoee. Group discounts at checkout.",
    bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/32936",
  },
];

// ----- Offerings (home cards) -----
export const offerings = [
  // CTA verbs unified to "Book / Reserve / View" (item 48).
  // Prices labelled with USD (item 85). Booking jargon "TheFlyBook" replaced (item 87).
  // Class range normalized to III–IV (audit 2026-07) — the Ocoee's documented
  // whitewater is predominantly Class III–IV; we don't advertise Class V.
  // New round #35: every CTA starts with "View" (informational) or "Book/Reserve" (transactional). Same price-from + meta pattern.
  // #38: every card carries a "from" price line where applicable.
  // #40: bunkhouse phrased as plain English, not a math expression.
  {
    slug: "rafting",
    label: "Whitewater",
    title: "River Trips",
    desc: "Guided rafting on the Middle Ocoee — the river that hosted the 1996 Olympic whitewater events.",
    img: "real/rafting/_DSC2913.jpg",
    cta: "Book a Trip",
    link: "/rafting",
    meta: "Class III–IV · Middle & Upper Olympic",
    // NEEDS-VERIFY: rafting trip pricing — operator hasn't confirmed a public
    // price floor. Until they do, the card shows "Call for pricing" instead
    // of inventing a number. Previous "$89" was fabricated and got pulled.
    priceFrom: "Call for pricing",
  },
  {
    slug: "lodge",
    label: "Lodging",
    title: "Ocoee Whitewater Lodge",
    // Soft description — room types and 2-night minimum still NEEDS-VERIFY in business.ts.
    desc: "On-site lodging near the Ocoee River.",
    img: "real/lodge/_DSC5460.jpg",
    cta: "See Rates",
    link: "/lodge",
    meta: "On-site lodging",
    // Price + min-stay shown only on the Detail page; not asserted on the
    // home cards because the home of the original site doesn't.
    priceFrom: "Rates on request",
  },
  {
    slug: "bunkhouse",
    label: "Lodging",
    title: "The Bunkhouse",
    desc: "Group-friendly bunk lodging on the property.",
    img: "real/bunkhouse/20250321_175920815_iOS.jpg",
    cta: "Reserve a Bunk",
    link: "/bunkhouse",
    meta: "On-site · groups",
    priceFrom: "Rates on request",
  },
  {
    slug: "camping",
    label: "Outdoors",
    title: "Camping & RV",
    // Pavilions claim removed — confirm in business.ts before re-adding.
    desc: "Tent sites and RV hookups on the property.",
    img: "real/pretrip/IMG_6166.jpg",
    cta: "Reserve a Site",
    link: "/camping",
    meta: "Tent · RV",
    priceFrom: "Rates on request",
  },
  {
    slug: "sugarloaf",
    label: "Dining",
    title: "Sugarloaf Bar & Grill",
    desc: "On-site bar and grill at the lodge.",
    // Real photo — pimento cheese chicken sandwich, signature look of the grill.
    img: "menu/sugarloaf/dishes/pimento-chicken-sandwich.jpg",
    cta: "View Hours",
    link: "/sugarloaf",
    meta: "On-site",
    priceFrom: "À la carte",
  },
  {
    slug: "bbq",
    label: "Dining",
    title: "Blue Hole BBQ",
    desc: "Smoked BBQ at a separate location on US Hwy 64, a short drive from the lodge.",
    // Real photo — brisket philly cheesesteak, BBQ menu signature.
    img: "menu/bbq/dishes/brisket-philly.jpg",
    cta: "View BBQ Menu",
    link: "/bbq",
    meta: "Off-site",
    priceFrom: "À la carte",
  },
  {
    slug: "hikes",
    label: "Free",
    title: "Hikes & Waterfalls",
    // "All year round" removed — seasonal closures apply.
    desc: "Trails and waterfalls in the surrounding Cherokee National Forest.",
    img: "images/generated-1777507427104.png",
    cta: "View Trail Map",
    link: "/hikes",
    meta: "Cherokee National Forest",
    priceFrom: "Free public access",
  },
  {
    slug: "events",
    label: "Groups",
    title: "Events",
    desc: "Group trips and event planning built around the river.",
    // Real photo — family-style table spread (ribs, pulled meat, sides, biscuits).
    // Sells the "feed a crew" promise better than a single rafting frame.
    img: "menu/catering/family-spread.jpg",
    cta: "Get in Touch",
    link: "/events",
    meta: "Custom",
    priceFrom: "Quote on request",
  },
] as const;

export const navLinks = [
  { label: "Home",            href: "/" },
  { label: "Rafting",         href: "/rafting" },
  { label: "Lodge",           href: "/lodge" },
  { label: "Bunkhouse",       href: "/bunkhouse" },
  { label: "Camping",         href: "/camping" },
  { label: "Sugarloaf",       href: "/sugarloaf" },
  { label: "Blue Hole BBQ",   href: "/bbq" },
  { label: "Hikes",           href: "/hikes" },
  { label: "Gallery",         href: "/gallery" },
  { label: "Events",          href: "/events" },
  { label: "Shop",            href: "/shop" },
  { label: "Contact",         href: "/contact" },
] as const;

// ----- Per-page detail content (used by Detail.tsx) -----
export type PageContent = {
  slug: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  hero: string;
  body: string[];
  features: string[];
  ctaLabel: string;
  meta: string;
  bookingUrl?: string;
  // When this page lives at a different physical address than the lodge campus.
  addressOverride?: string;
  // When this venue has its own phone line, distinct from the lodge's main
  // number (e.g. Blue Hole BBQ runs from a separate kitchen up the road).
  phoneOverride?: string;
  phoneRawOverride?: string;
};

export const pages: Record<string, PageContent> = {
  rafting: {
    slug: "rafting",
    eyebrow: "WHITEWATER · MIDDLE & UPPER OCOEE",
    title: "Class III–IV Rapids on an Olympic-grade River",
    subtitle:
      "Two trips on the river that hosted the 1996 Olympic whitewater events.",
    hero: "/images/generated-1777507417228.png",
    body: [
      "Two trips, two flavors. The Middle Ocoee is the classic — five miles of Class III–IV whitewater through the Cherokee National Forest. The Full River combo adds the Olympic course (Upper Ocoee) above it for a longer, harder day.",
      "Trips run on the river's dam-release days — water doesn't flow every day, so call or check the booking calendar to lock dates. Every trip is guided.",
      // Removed: "Helmets, PFDs, paddles included; wetsuits when cold" — left
      // for the operator to confirm exactly which items are included on
      // their current trips. Included gear lives in features list (verified).
    ],
    // Features list trimmed to claims sourced from the original site,
    // public knowledge about the Ocoee, or the business's own published
    // copy. Anything operations-specific (guide credentials, photo pack
    // pricing, age, weight, cancellation tiers, shuttle availability,
    // discount programs) is marked NEEDS-VERIFY and dropped from this
    // list so the marketing copy never asserts something we can't prove.
    features: [
      "Middle Ocoee · Class III–IV · 5-mile run", // public-fact (well-documented for the Ocoee)
      "Olympic Upper + Middle Ocoee combo trip", // verified-original (mentioned in nav)
      "Guided trips with gear included", // verified-original (booking copy)
      "Free on-site parking", // verified-original (homepage)
      "Trips run on the TVA Ocoee release schedule", // public-fact
      // NEEDS-VERIFY (drop until business confirms):
      //   "USCG & ACA-licensed guides"
      //   "CPR + First Aid certified safety briefing"
      //   "Photo pack add-on" (price + availability)
      //   "Charter shuttle for groups of 10+"
      //   "Military / first-responder / kids 12–15 discounts"
      //   "Cancellation: full refund 7+ days · credit at 48 hrs"
      //   Specific minimum age numbers (12 / 16)
      //   Class V rating on Upper Olympic (typically Class IV–V)
    ],
    ctaLabel: "Book on TheFlyBook",
    meta: "Rafting · Middle & Upper Ocoee · Cherokee National Forest, TN",
    bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/32930",
  },
  lodge: {
    slug: "lodge",
    eyebrow: "LODGING · ON-SITE",
    title: "Ocoee Whitewater Lodge",
    // Specific room counts, rates, and minimum-stay policy are still
    // NEEDS-VERIFY upstream (`lodge` const). This page intentionally does
    // not assert them — links to booking instead.
    subtitle: "On-site lodging conveniently located near the Ocoee River.",
    hero: "/images/generated-1777507485565.png",
    body: [
      "The Ocoee Whitewater Lodge sits at 414 Parksville Road, a short distance to Parksville Lake and the Ocoee River. Room types and current availability are managed through our booking system.",
      "Call us or check availability online for current rates, room composition, and minimum-stay requirements.",
    ],
    features: [
      "Conveniently located near the Ocoee River",
      "Climate control",
      "Private bathroom in every room",
      "Free Wi-Fi",
    ],
    ctaLabel: "Check Availability",
    meta: "Lodging · Ocoee Whitewater Lodge · Benton TN",
    // Lodging entity 33052 — verified against the live "Click Here to Reserve"
    // button on ocoeeriverexperience.com/general-1. Previously linked 33050
    // by mistake (that ID is "RV Sites Power & Water" in the camping catalog).
    bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33052",
  },
  bunkhouse: {
    slug: "bunkhouse",
    eyebrow: "LODGING · GROUPS",
    title: "The Bunkhouse",
    // Capacity (44 / 11) and amenities (A/C, BYO bedding, ladies-only room)
    // are NEEDS-VERIFY upstream. Page softened to describe the offering
    // without asserting specific counts.
    subtitle: "Group-friendly bunk lodging on the property.",
    hero: "/images/generated-1777507418785.png",
    body: [
      "The Bunkhouse is the affordable way to bring a crew. Bunks can be booked individually for a communal stay or as a whole room for private groups — call or book online for current capacity and availability.",
    ],
    features: [
      "On the property at 414 Parksville Road",
      "Group-friendly setup",
      "Book individual bunks or a whole room",
    ],
    ctaLabel: "Reserve a Bunk",
    meta: "Lodging · Bunkhouse",
    bookingUrl: "https://go.theflybook.com/book/1452/EntityConfig/33053",
  },
  camping: {
    slug: "camping",
    eyebrow: "OUTDOORS · CAMPING & RV",
    title: "Camping & RV",
    // Specific rates and the existence of pavilions are still NEEDS-VERIFY
    // upstream. Page now describes the offering generically and asks the
    // visitor to call for current pricing.
    subtitle: "Tent sites and RV hookups on the property, near the Ocoee River.",
    hero: "/images/generated-1777507418785.png",
    body: [
      "Pitch a tent or pull in the rig — sites sit on the property, a short distance to Parksville Lake and the Ocoee River. Call us for current rates and availability before booking.",
    ],
    features: [
      "Tent sites on the property",
      "RV hookups",
      "Short distance to Parksville Lake and the Ocoee River",
    ],
    ctaLabel: "Reserve a Site",
    meta: "Camping · RV · Tent",
    // The /camping detail page renders the 4 sub-product cards (RV 50A,
    // RV Power+Water, Tent, Pavillions) each with their own entity-specific
    // CTA — this `bookingUrl` is the fallback / catalog landing for the
    // top-of-page primary CTA when the visitor wants to see everything.
    bookingUrl: "https://go.theflybook.com/book/1452",
  },
  sugarloaf: {
    slug: "sugarloaf",
    eyebrow: "DINING · ON-SITE",
    title: "Sugarloaf Bar & Grill",
    // Menu and exact hours are NEEDS-VERIFY upstream. The page used to
    // assert specific menu items + Friday 12-10 / Saturday 8-10 / Sunday 8-6,
    // none of which appear on the original homepage. Softened.
    subtitle: "On-site bar and grill at the lodge.",
    // Real operator photo replaces the previously-dark Remotion video.
    // The pimento chicken sandwich is the venue's signature plate; it
    // sells the dining identity in one frame.
    hero: "/menu/sugarloaf/dishes/pimento-chicken-sandwich.jpg",
    body: [
      "Sugarloaf sits on the same property as the lodge — no driving after the river. Hours and the current menu run seasonally; call ahead in spring and fall.",
    ],
    features: [
      "On-site at 414 Parksville Road",
      "Seasonal hours — call ahead in spring and fall",
    ],
    ctaLabel: "Call to Confirm Hours",
    meta: "Dining · On-site",
  },
  bbq: {
    slug: "bbq",
    // Shortened — "DINING · OFF-SITE LOCATION" was wrapping ugly on mobile,
    // leaving "LOCATION" alone on a second line. The "off-site" framing
    // lives in the page subtitle/body where it has room to breathe.
    eyebrow: "DINING · OFF-SITE",
    title: "Blue Hole BBQ",
    // Style descriptor ("Tennessee-style smoked BBQ"), specific menu items,
    // hours, perks, and the McDonald's landmark are all NEEDS-VERIFY in
    // `bbq` const. Subtitle softened, features list trimmed.
    subtitle:
      "Smoked BBQ at a separate location on US Highway 64 — about a short drive from the lodge.",
    hero: "/images/generated-1777507682015.png",
    body: [
      "Blue Hole BBQ runs out of a separate kitchen up the road from the lodge. Hours, menu, and pickup options can change seasonally — confirm before driving over.",
    ],
    features: [
      "130 US Highway 64, Ocoee, TN 37361",
      "Off-site — short drive from the lodge",
      "Order online for pickup when available",
    ],
    ctaLabel: "View BBQ Details",
    meta: "Dining · BBQ · Off-site",
    addressOverride: "130 US Highway 64, Ocoee, TN 37361",
    // Blue Hole BBQ has its own line, separate from the lodge's main number.
    phoneOverride: "(423) 769-2012",
    phoneRawOverride: "+14237692012",
  },
  hikes: {
    slug: "hikes",
    eyebrow: "OUTDOORS · CHEROKEE NATIONAL FOREST",
    title: "Hikes & Waterfalls",
    subtitle: "Trails and waterfalls in the surrounding Cherokee National Forest.",
    hero: "/images/generated-1777507427104.png",
    body: [
      "The Cherokee National Forest surrounds the property. Access is free; stop by the front desk and we'll point you to our favorite routes, plus the trail conditions and maps published by the U.S. Forest Service.",
    ],
    // "Open all year" removed — Cherokee NF trails close seasonally for
    // wildlife management and weather. We don't claim year-round access.
    features: [
      "Cherokee National Forest access",
      "Waterfall trails nearby",
      "Trail maps & current conditions at the front desk",
    ],
    ctaLabel: "Ask About Trails",
    meta: "Hikes · Waterfalls · Cherokee National Forest",
  },
  events: {
    slug: "events",
    eyebrow: "GROUPS · CUSTOM",
    title: "Events & Group Trips",
    // Soft copy — original site does not enumerate weddings / team-building /
    // scouts as separate offerings. We list "events" generically and ask the
    // visitor to call so the operator can scope the actual program.
    subtitle: "Group trips and event planning built around the river.",
    hero: "/images/generated-1777507682015.png",
    body: [
      "Planning a group trip, a corporate retreat, a wedding party, a scout outing or a school visit? Contact us and we'll work out the details together — lodging, dining, rafting, and the river are all on one property.",
    ],
    features: [
      "Group trips",
      "Custom multi-day schedules",
      "Lodging + dining + rafting on one property",
    ],
    ctaLabel: "Get in Touch",
    meta: "Events · Groups",
  },
};

/* =====================================================================
   Google reviews — live integration via Vercel Edge Function.
   Russ asked (2026-05-23) for review text to come directly from Google,
   not copy-pasted. The reviews are fetched at request time from
   `/api/google-reviews` (see `api/google-reviews.ts`) which proxies the
   Google Places API. Key + Place ID live in Vercel env vars; nothing
   secret ships in this file.

   Source URL (Russ's Google Business Profile):
     https://maps.app.goo.gl/Ws94mdWBjf16uTqv5
   Public canonical:
     https://www.google.com/maps/place/Ocoee+River+Experience,+LLC

   Setup (Russ — one-time, in Vercel dashboard → Settings → Environment
   Variables, set for BOTH Production and Preview):

     GOOGLE_PLACES_API_KEY
        Get from Google Cloud Console:
          1. https://console.cloud.google.com → enable "Places API (New)"
          2. Create an API key restricted to: Places API only, no HTTP
             referrer restriction (server-side use)
          3. Set monthly quota cap (~$10) so a misconfig can't spike
             the bill — Places Details = $0.017/call, with our 24h
             cache that's ~$0.50/month at normal traffic.

     GOOGLE_PLACE_ID
        Resolve the lodge's Place ID once:
          1. Go to https://developers.google.com/maps/documentation/places/web-service/place-id
          2. Search "Ocoee River Experience LLC, Benton TN" — the
             green pin is the lodge address (414 Parksville Road).
          3. Copy the Place ID (looks like "ChIJ..." — about 27 chars).

   Until both env vars are set, /api/google-reviews returns
   { reviews: [], status: "unconfigured" } and the <Reviews /> component
   renders nothing — safe to ship today.
   ===================================================================== */
export const reviewsSourceUrl =
  "https://www.google.com/maps/place/Ocoee+River+Experience,+LLC/@35.1097993,-84.6553959,17z";
